<template>
    <div class="appointments">
        <template v-if="recordCreated || recordUpdated">
            <AppointmentsCreated :project-id="lastCreatedRecordData.projectId"
                                 :client-phone="lastCreatedRecordData.clientPhone"
                                 :record-date="lastCreatedRecordData.recordDate"
                                 :client-name="lastCreatedRecordData.clientName"
                                 :client-surname="lastCreatedRecordData.clientSurname"
                                 :is-after-update="recordUpdated && !recordCreated"
                                 :comment="lastCreatedRecordData.comment"
                                 @restartCreating="(recordCreated && !recordUpdated) ? createNewAppointment() : () => null"
                                 @goToAppointments="fetchAppointments" />
        </template>
        <template v-else-if="recordNotCreated || recordNotUpdated">
            <AppointmentsNotCreated :is-after-update="recordNotUpdated && !recordNotCreated"
                                    @restartCreating="(recordNotCreated && !recordNotUpdated) ? createNewAppointment() : () => null"
                                    @goToAppointments="fetchAppointments" />
        </template>
        <template v-else>
            <div class="appointments__top">
                <div class="appointments__top-title">
                    <div v-if="isChangingAppointment"
                         class="appointments__top-changing-title">
                        <h1>Перенос записи</h1>
                        <div class="appointments__top-changing-description">
                            {{ changingTitle }}
                        </div>
                    </div>
                    <h1 v-else>Записаться в офис</h1>
                </div>

                <div v-if="isCreatingNewAppointment || isChangingAppointment"
                     class="appointments__top-cancel">
                    <STBaseButton :text="isCreatingNewAppointment ? 'Отмена записи' : 'Отмена'"
                                  modify="button_theme_reset"
                                  @click="attemptToCancelRecord" />
                </div>
            </div>
            <template v-if="!preload">
                <template v-if="isCreatingNewAppointment">
                    <div class="appointments__creating-wrapper">
                        <AppointmentsCreating :external-step="externalStep"
                                              :external-project-id="externalProjectId"
                                              :external-client-phone="externalClientPhone"
                                              :external-client-name="externalClientName"
                                              :external-client-surname="externalClientSurname"
                                              @recordCreated="setRecordCreated"
                                              @recordNotCreated="setRecordNotCreated"
                                              @cancel="attemptToCancelRecord" />
                    </div>
                </template>
                <template v-else-if="isChangingAppointment">
                    <div class="appointments__changing-wrapper">
                        <AppointmentsChanging :visit-record-id="visitIdToChange"
                                              :project-id="externalProjectId"
                                              :client-phone="externalClientPhone"
                                              :client-name="externalClientName"
                                              :client-surname="externalClientSurname"
                                              :external-comment="externalComment"
                                              @recordUpdated="setRecordUpdated"
                                              @recordNotUpdated="setRecordNotUpdated"
                                              @cancel="attemptToCancelRecord" />
                    </div>
                </template>
                <template v-else>
                    <div v-if="isEmpty || (!isError && !appointments?.length)"
                         class="appointments__empty-wrapper">
                        <AppointmentsEmpty>
                            <template v-slot:title>
                                Здесь пока ничего нет
                            </template>
                            <template v-slot:text>
                                Пока у&nbsp;вас нет назначенных посещений.
                                <br>
                                Запишитесь, нажав на&nbsp;кнопку ниже.
                            </template>
                            <template v-slot:footer>
                                <STBaseButton text="Записаться в офис"
                                              icon-after="IconRightFill"
                                              icon-after-modify="icon-fill icon_color_white"
                                              @click="createNewAppointment" />
                            </template>
                        </AppointmentsEmpty>
                    </div>
                    <div v-else-if="isError">
                        <AppointmentsEmpty>
                            <template v-slot:title>
                                Ошибка
                            </template>
                            <template v-slot:text>
                                К сожалению, произошла техническая ошибка.
                                <br>
                                Попробуйте еще раз позже или перезагрузите страницу.
                            </template>
                            <template v-slot:footer>
                                <STBaseButton text="Попробовать снова"
                                              @click="fetchAppointments" />
                            </template>
                        </AppointmentsEmpty>
                    </div>
                    <div v-else
                         class="appointments__records-wrapper">
                        <AppointmentsRecords :records="appointments"
                                             @update="fetchAppointments"
                                             @create="createNewAppointment"
                                             @change="changeAppointment" />
                    </div>
                </template>
            </template>
            <template v-else>
                <div class="appointments__loading">
                    <IconLoad class="appointments__loader" />
                    <div class="appointments__loading-text">
                        Ищем записи...
                    </div>
                </div>
            </template>
        </template>

        <STPopup ref="cancelAppointmentsPopup"
                 :icon-close-big="false"
                 modify="popup_middle_size popup_center_position">
            <AppointmentsCancel @submitRecordCancelling="submitRecordCancelling"
                                @close="closeAppointmentsCancelPopup" />
        </STPopup>
    </div>
</template>

<script>
import IconLoad from '@/components/Icons/LoadGrey';
import AppointmentsEmpty from '@/blocks/AppointmentsBlock/AppointmentsEmpty/AppointmentsEmpty';
import AppointmentsRecords from '@/blocks/AppointmentsBlock/AppointmentsRecords/AppointmentsRecords';
import AppointmentsCreating from '@/blocks/AppointmentsBlock/AppointmentsCreating/AppointmentsCreating';
import AppointmentsChanging from '@/blocks/AppointmentsBlock/AppointmentsChanging/AppointmentsChanging';
import AppointmentsCancel from '@/blocks/AppointmentsBlock/AppointmentsCancel/AppointmentsCancel';
import AppointmentsCreated from '@/blocks/AppointmentsBlock/AppointmentsCreated/AppointmentsCreated';
import AppointmentsNotCreated from '@/blocks/AppointmentsBlock/AppointmentsNotCreated/AppointmentsNotCreated';

import {mapActions} from 'vuex';

const HIDDEN_APPOINTMENT_STATUSES = new Set(['cancelled', 'canceled', 'completed', 'finished']);

export default {
    name: 'Appointments',

    components: {
        IconLoad,
        AppointmentsCancel,
        AppointmentsEmpty,
        AppointmentsRecords,
        AppointmentsCreating,
        AppointmentsChanging,
        AppointmentsCreated,
        AppointmentsNotCreated
    },

    data() {
        return {
            preload                 : true, // показываем ли прелоадер
            isEmpty                 : true,
            isError                 : false,
            isCreatingNewAppointment: false,
            isChangingAppointment   : false,
            recordCreated           : false,
            recordUpdated           : false,
            recordNotCreated        : false,
            recordNotUpdated        : false,
            externalStep            : null,
            externalProjectId       : null,
            externalClientPhone     : null,
            externalClientName      : null,
            externalClientSurname   : null,
            externalComment         : null,
            lastCreatedRecordData   : null,
            appointments            : [],
            visitIdToChange         : null,
            changingTitle           : null,
        };
    },

    created() {
        const routeAppointmentData = this.$route?.params?.appointmentData;

        if (routeAppointmentData?.isCreatingNewAppointment) {
            this.createNewAppointment(routeAppointmentData?.step || null, routeAppointmentData?.projectId || null, null, routeAppointmentData?.clientName || null, routeAppointmentData?.clientSurname || null);
        }

        document.addEventListener('createAppointmentFromSidebar', this.createAppointmentFromExternalComponent);
    },

    async mounted() {
        await this.fetchAppointments();
    },

    beforeDestroy() {
        document.removeEventListener('createAppointmentFromSidebar', this.createAppointmentFromExternalComponent);
    },

    methods: {
        ...mapActions(['getAppointments']),

        async fetchAppointments() {
            this.reInitPageData();
            this.preload = true;

            try {
                const result = await this.getAppointments();
                const filteredRecords = result.data.records.filter(
                    record => !HIDDEN_APPOINTMENT_STATUSES.has(record.status?.toLowerCase())
                );
                this.preload = false;

                if (filteredRecords?.length) {
                    this.appointments = filteredRecords;
                } else {
                    this.isEmpty = true;
                    this.appointments = [];
                }
            } catch(error) {
                this.preload = false;
                this.appointments = [];
                this.isError = true;
                console.error('Ошибка получения записей на презентацию:', JSON.stringify(error));
            }
        },

        reInitPageData() {
            this.recordCreated = false;
            this.recordUpdated = false;
            this.recordNotCreated = false;
            this.recordNotUpdated = false;
            this.preload = false;
            this.isEmpty = false;
            this.isCreatingNewAppointment = false;
            this.isChangingAppointment = false;
            this.externalStep = null;
            this.externalClientName = null;
            this.externalClientSurname = null;
            this.externalProjectId = null;
            this.externalClientPhone = null;
            this.externalComment = null;
            this.lastCreatedRecordData = null;
            this.visitIdToChange = null;
            this.changingTitle = null;
        },

        createNewAppointment(step = null, projectId = null, clientPhone = null, clientName = null, clientSurname = null) {
            this.reInitPageData();
            this.isCreatingNewAppointment = true;
            this.externalStep = step;
            this.externalProjectId = projectId;
            this.externalClientPhone = clientPhone;
            this.externalClientName = clientName;
            this.externalClientSurname = clientSurname;
        },

        changeAppointment(visitRecordId = null, changingTitle = null, projectId = null, clientPhone = null, clientName = null, clientSurname = null, comment = null) {
            this.reInitPageData();
            this.isChangingAppointment = true;
            this.visitIdToChange = visitRecordId;
            this.changingTitle = changingTitle;
            this.externalProjectId = projectId;
            this.externalClientPhone = clientPhone;
            this.externalClientName = clientName;
            this.externalClientSurname = clientSurname;
            this.externalComment = comment;
        },

        setRecordCreated(lastCreatedRecordData = null) {
            this.reInitPageData();
            this.recordCreated = true;
            this.lastCreatedRecordData = lastCreatedRecordData;
        },

        setRecordUpdated(lastCreatedRecordData = null) {
            this.reInitPageData();
            this.recordUpdated = true;
            this.lastCreatedRecordData = lastCreatedRecordData;
        },

        setRecordNotCreated() {
            this.reInitPageData();
            this.recordNotCreated = true;
        },

        setRecordNotUpdated() {
            this.reInitPageData();
            this.recordNotUpdated = true;
        },

        attemptToCancelRecord() {
            this.$refs.cancelAppointmentsPopup.open();
        },

        submitRecordCancelling() {
            this.isCreatingNewAppointment = false;
            this.isChangingAppointment = false;
            this.lastCreatedRecordData = null;
            this.closeAppointmentsCancelPopup();
        },

        closeAppointmentsCancelPopup() {
            this.cancelId = null;
            this.$refs.cancelAppointmentsPopup.close();
        },

        createAppointmentFromExternalComponent(data = null) {
            this.createNewAppointment(data?.detail?.step || null,
                data?.detail?.projectId || null,
                data?.detail?.clientPhone || null,
                        data?.detail?.clientName || null,
                data?.detail?.clientSurname || null);
        }
    }
};
</script>

<style lang="scss">
@import "Appointments";
</style>
